"use client";

import React, { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./AppointmentCard.css";
import ManageAppointmentModal from "../Manageappointmentmodal/Manageappointmentmodal";

const isValidImage = (src) => {
  if (!src || typeof src !== "string") return false;
  const v = src.trim().toLowerCase();
  return v !== "" && v !== "null" && v !== "undefined" && v !== "false";
};

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0]?.slice(0, 2).toUpperCase() || "PT";
};

const formatDate = (item) => {
  const value = item.startTime || item.date;
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(item.date || "—");
  return d.toLocaleDateString("fr-FR");
};

const formatTime = (item) => {
  if (item.time) return item.time;
  const value = item.startTime || item.date;
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};

/* ── Dropdown menu 3 points ── */
function ActionsDropdown({ onConfirm, onCancel, onManage, isPending }) {
  const { t } = useTranslation("appointments");
  const [open, setOpen]       = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const triggerRef            = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [open]);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top:   rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((v) => !v);
  };

  return (
    <div className="appt-dropdown" ref={triggerRef}>
      <button
        type="button"
        className="appt-dropdown__trigger"
        onClick={handleToggle}
        aria-label={t("table.actions")}
        aria-expanded={open}
      >
        <span />
        <span />
        <span />
      </button>

      {open && (
        <div
          className="appt-dropdown__menu"
          style={{ top: menuPos.top, right: menuPos.right }}
        >
          {isPending ? (
            <>
              <button
                type="button"
                className="appt-dropdown__item appt-dropdown__item--confirm"
                onClick={() => { setOpen(false); onConfirm?.(); }}
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M4 10l4 4 8-8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t("card.confirm")}
              </button>

              <button
                type="button"
                className="appt-dropdown__item"
                onClick={() => { setOpen(false); onManage?.(); }}
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M4 6h12M4 10h8M4 14h5" strokeLinecap="round" />
                </svg>
                {t("card.manage")}
              </button>

              <div className="appt-dropdown__divider" />

              <button
                type="button"
                className="appt-dropdown__item appt-dropdown__item--danger"
                onClick={() => { setOpen(false); onCancel?.(); }}
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M6 6l8 8M14 6l-8 8" strokeLinecap="round" />
                </svg>
                {t("card.cancel")}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="appt-dropdown__item"
              onClick={() => { setOpen(false); onManage?.(); }}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4 6h12M4 10h8M4 14h5" strokeLinecap="round" />
              </svg>
              {t("card.viewDetails")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main component ── */
export default function AppointmentCard({
  item,
  medecinId,
  busy,
  onConfirm,
  onCancel,
  onManage,
  onReschedule,
  viewMode = "grid",
}) {
  const { t } = useTranslation("appointments");
  const [showManage, setShowManage] = useState(false);
  const [imgError, setImgError]     = useState(false);

  const initials = useMemo(() => getInitials(item.name), [item.name]);
  const imageSrc = useMemo(
    () => item.photo || item.avatar || item.image || item.patient?.photo || "",
    [item]
  );
  const hasImage = isValidImage(imageSrc) && !imgError;

  const avatar = (
    <div className="appointment-card__avatar">
      {hasImage ? (
        <img src={imageSrc} alt={item.name || "Patient"} onError={() => setImgError(true)} />
      ) : (
        <div className="appointment-card__avatar-fallback">{initials}</div>
      )}
    </div>
  );

  const status        = String(item.status || "").toLowerCase();
  const isConfirmed   = status === "confirmed" || status === "rescheduled";
  const isRescheduled = status === "rescheduled";
  const isCancelled   = status === "cancelled";
  const isCompleted   = status === "completed";
  const isPending     = !isConfirmed && !isCancelled && !isCompleted;

  const statusLabel = isRescheduled ? t("status.rescheduled")
    : isConfirmed  ? t("status.confirmed")
    : isCancelled  ? t("status.cancelled")
    : isCompleted  ? t("status.completed")
    : t("status.pending");

  const statusBadge = (
    <span className={[
      "appointment-card__status-pill",
      isConfirmed && !isRescheduled ? "is-confirmed"   : "",
      isRescheduled                 ? "is-rescheduled" : "",
      isCancelled                   ? "is-cancelled"   : "",
      isCompleted                   ? "is-completed"   : "",
      isPending                     ? "is-pending"     : "",
    ].join(" ")}>
      {statusLabel}
    </span>
  );

  /* Grid card actions */
  const gridActions = isPending ? (
    <>
      <button
        className="appointment-card__confirm"
        onClick={onConfirm}
        disabled={busy === "accept"}
      >
        {busy === "accept" ? "..." : t("card.confirm")}
      </button>
      <button
        className="appointment-card__manage"
        type="button"
        onClick={() => setShowManage(true)}
      >
        {t("card.manage")}
      </button>
      <button
        className="appointment-card__cancel"
        type="button"
        onClick={onCancel}
        disabled={busy === "cancel"}
      >
        {busy === "cancel" ? "..." : "×"}
      </button>
    </>
  ) : statusBadge;

  const manageModal = showManage ? (
    <ManageAppointmentModal
      item={item}
      medecinId={medecinId}
      onClose={() => setShowManage(false)}
      onAccept={onConfirm}
      onCancel={onCancel}
      onReschedule={onReschedule}
    />
  ) : null;

  /* ── LIST view ── */
  if (viewMode === "list") {
    return (
      <>
        <div className="appointment-row">
          <div className="appointment-row__patient">
            {avatar}
            <div className="appointment-card__patient-info">
              <h4>{item.name || "Patient"}</h4>
              {item.email && <p>{item.email}</p>}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ color: "#546881", fontSize: 11 }}>
                  {String(item.source || "medilink").toLowerCase() === "local"
                    ? "Local"
                    : item.subprofileId || String(item.source).toLowerCase() === "subprofile"
                    ? "Subprofile"
                    : "Medilink"}
                </span>
                {item.identifier && <span>{item.identifier}</span>}
              </div>
            </div>
          </div>

          <div className="appointment-row__datetime">
            <span>{formatDate(item)}</span>
            <small>{formatTime(item)}</small>
          </div>

          <div className="appointment-row__reason">
            {item.reason || item.state || t("card.consultation")}
          </div>

          <div className="appointment-row__status">{statusBadge}</div>

          <div className="appointment-row__actions">
            <ActionsDropdown
              isPending={isPending}
              onConfirm={onConfirm}
              onCancel={onCancel}
              onManage={() => setShowManage(true)}
            />
          </div>
        </div>

        {manageModal}
      </>
    );
  }

  /* ── GRID view ── */
  return (
    <>
      <div className="appointment-card">
        <div className="appointment-card__patient">
          {avatar}
          <div className="appointment-card__patient-info">
            <h4>{item.name || "Patient"}</h4>
              {item.email && <p>{item.email}</p>}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ color: "#546881", fontSize: 11 }}>{item.source ? (String(item.source).toLowerCase() === "local" ? "Local" : "Medilink") : "Medilink"}</span>
                {item.identifier && <span>{item.identifier}</span>}
              </div>
          </div>
        </div>

        <div className="appointment-card__details">
          <p>{formatDate(item)}</p>
          <p>{formatTime(item)}</p>
          {item.reason ? (
            <p className="appointment-card__reason">
              <span className="appointment-card__reason-label">
                {t("card.reason")}:{" "}
              </span>
              {item.reason}
            </p>
          ) : (
            <p className="appointment-card__reason appointment-card__reason--empty">
              {item.state || t("card.consultation")}
            </p>
          )}
        </div>

        <div className="appointment-card__actions">{gridActions}</div>
      </div>

      {manageModal}
    </>
  );
}